import React, { useState } from 'react';
import { css } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { KeyNames } from '../../constants';
import { EditIcon, ListsIcon, MoreIcon, TrashIcon } from '../../icon';
import { styles } from '../../styles';
import { ButtonBar, PrimaryButton, SecondaryButton } from '../buttons';
import { useFormField } from '../forms';
import { IconButton } from '../icon-button';
import { PopoverMenu, PopoverMenuItem, usePopoverMenu } from '../popover';
import { Text } from '../text';

interface NoteProps {
  author: string;
  content?: string;
  dateCreated: string;
  editMode?: boolean;
  fallbackText?: string;
  onCancel?: () => void;
  onDelete?: () => void;
  onUpdate: (newVal: string) => void;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  moreButtonTrackingId?: string;
  formSubmitTrackingId?: string;
}

export const Note = ({
  author,
  content,
  dateCreated,
  editMode = false,
  fallbackText = 'Edit this note to add some text',
  onCancel,
  onDelete,
  onUpdate,
  primaryButtonText = 'Save',
  secondaryButtonText = 'Cancel',
  moreButtonTrackingId,
  formSubmitTrackingId,
}: NoteProps) => {
  const [edit, setEdit] = useState(editMode);
  const popoverMenuProps = usePopoverMenu();
  const formProps = useFormField({ type: 'text', value: content });

  const handleSubmit = () => {
    onUpdate(formProps.value);
    setEdit(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === KeyNames.Escape) {
      setEdit(false);
      e.stopPropagation();
    }
    if (!e.shiftKey && e.key === KeyNames.Enter) {
      handleSubmit();
    }
  };

  return (
    <article className={noteArticleStyle}>
      <div className={noteHeaderStyle}>
        <Text size='large' weight='semibold' className={noteAuthorTextStyle}>
          <ListsIcon className={noteAuthorIconStyle} />
          {author}
        </Text>
        <Text size='medium' className={noteResetMarginStyle}>
          <time dateTime={dateCreated}>{dateCreated}</time>
        </Text>
      </div>
      {edit ? (
        <form onSubmit={handleSubmit} data-trackingid={formSubmitTrackingId}>
          <textarea
            autoFocus
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              formProps.onChange(e);
            }}
            onKeyDown={handleKeyDown}
            value={formProps.value}
            className={noteTextareaStyle}
          />
          <ButtonBar css={{ padding: 0 }}>
            <SecondaryButton
              css={{ width: 'auto' }}
              onClick={() => {
                onCancel?.();
                setEdit(false);
              }}
            >
              {secondaryButtonText}
            </SecondaryButton>
            <PrimaryButton css={{ width: 'auto' }} disabled={formProps.value.length < 1} type='submit'>
              {primaryButtonText}
            </PrimaryButton>
          </ButtonBar>
        </form>
      ) : (
        <span className={noteContentSpanStyle}>
          <Text className={noteContentTextStyle} css={styles.multilineTruncate(3)} color='light'>
            {content ? content : fallbackText}
          </Text>
          <IconButton
            {...popoverMenuProps.getTriggerProps()}
            label='more'
            className={noteMoreButtonStyle}
            trackingId={moreButtonTrackingId}
          >
            <MoreIcon />
          </IconButton>
        </span>
      )}
      <PopoverMenu {...popoverMenuProps.getMenuProps()} title=''>
        <PopoverMenuItem
          {...popoverMenuProps.getItemProps({ index: 0 })}
          onClick={() => {
            popoverMenuProps.close();
            setEdit(true);
          }}
          Icon={EditIcon}
        >
          Edit
        </PopoverMenuItem>
        <PopoverMenuItem
          {...popoverMenuProps.getItemProps({ index: 1 })}
          onClick={() => {
            onDelete?.();
            popoverMenuProps.close();
          }}
          Icon={TrashIcon}
        >
          Delete
        </PopoverMenuItem>
      </PopoverMenu>
    </article>
  );
};

const noteArticleStyle = css`
  border-bottom: 1px solid ${theme.colors.neutral20};
  padding: ${theme.spacing(2, 1)};
`;

const noteHeaderStyle = css`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const noteAuthorTextStyle = css`
  align-items: center;
  display: flex;
  margin: 0;
`;

const noteAuthorIconStyle = css`
  margin-right: ${theme.spacing(1)};
`;

const noteResetMarginStyle = css`
  margin: 0;
`;

const noteTextareaStyle = css`
  border: none;
  font-size: ${theme.fontSize(16)};
  margin: ${theme.spacing(1)};
  outline: none;
  resize: none;
  width: 100%;

  &::placeholder {
    color: ${theme.colors.neutral70};
  }
`;

const noteContentSpanStyle = css`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 0;
`;

const noteContentTextStyle = css`
  padding: ${theme.spacing(0.5)};
`;

const noteMoreButtonStyle = css`
  margin-left: ${theme.spacing(1)};
`;
